import {
  type ClientEntitlements,
  isAuthorized,
  isEntitled,
  type Permissions,
} from '@nursio-link/common/authorization';
import {
  ClipboardList,
  HeartHandshake,
  ListTodoIcon,
  MenuIcon,
  PlusIcon,
} from 'lucide-react';
import {filter, pipe} from 'ramda';
import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';

import {useGroups} from '@/hooks/useAuth';
import {useEntitlements} from '@/hooks/useEntitlements';
import {cn} from '@/utils/ui';

import {useSheetSidebarStore} from '../sidebars/useSheetSidebar';
import type {FloatingMenuItemProps} from './FloatingMenuItem';
import FloatingMenuItem from './FloatingMenuItem';

export type FloatingMenuItemElementItem = FloatingMenuItemProps & {
  permission?: Permissions;
  entitlement?: ClientEntitlements;
};

const FloatingMenu = () => {
  const {t} = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentGroups = useGroups();
  const entitlements = useEntitlements();

  const setMenuOpen = useSheetSidebarStore(state => state.setOpen);

  const items: FloatingMenuItemElementItem[] = [
    {
      text: t('Today'),
      icon: <ClipboardList />,
      path: 'today',
      permission: 'VIEW_MENU',
      className: 'sm:hidden',
    },
    {
      text: t('Tasks'),
      icon: <ListTodoIcon />,
      path: 'tasks',
      permission: 'VIEW_TASKS',
      entitlement: 'TASKS',
      className: 'sm:hidden',
    },
    {
      text: t('Commands'),
      icon: <PlusIcon />,
      onClick: () => {
        searchParams.append('commands', 'true');
        setSearchParams(searchParams);
      },
    },
    {
      text: t('Residents'),
      icon: <HeartHandshake />,
      path: 'residents',
      permission: 'VIEW_RESIDENTS',
      entitlement: 'RESIDENTS',
      className: 'sm:hidden',
    },
    {
      text: t('Menu'),
      icon: <MenuIcon />,
      onClick: () => setMenuOpen(true),
      className: 'sm:hidden',
      permission: 'VIEW_MENU',
    },
  ];

  const filterByAuthorization = currentGroups
    ? filter(({permission}: FloatingMenuItemElementItem) =>
        permission ? isAuthorized(currentGroups, permission) : true
      )
    : () => [];

  const filterByEntitlement = entitlements
    ? filter(({entitlement}: FloatingMenuItemElementItem) =>
        entitlement ? isEntitled(entitlements, entitlement) : true
      )
    : () => [];

  const permittedItems = pipe(
    filterByAuthorization,
    filterByEntitlement
  )(items) as FloatingMenuItemElementItem[];

  return (
    <div
      className={cn(
        'fixed bottom-6 z-10 flex flex-row items-center justify-center gap-2 rounded-full p-1',
        'left-1/2 -translate-x-1/2',
        'sm:right-6 sm:left-auto sm:translate-x-0',
        'glass'
      )}
    >
      {permittedItems.map(item => (
        <FloatingMenuItem
          key={item.text}
          text={item.text}
          icon={item.icon}
          path={item.path}
          onClick={item.onClick}
          className={item.className}
        />
      ))}
    </div>
  );
};

export default FloatingMenu;
